// ** Modules
import React from 'react';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// ** UI
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { Swiper as SwiperInstance } from 'swiper/types';

export type SwiperCarouselSlide = {
	key: React.Key;
	content: React.ReactNode;
	sx?: SxProps<Theme>;
};

export type SwiperCarouselControls = {
	canGoPrev: boolean;
	canGoNext: boolean;
	activeIndicator: number;
	goPrev: () => void;
	goNext: () => void;
};

export type SwiperCarouselProps = {
	slides: SwiperCarouselSlide[];
	spaceBetween?: number;
	indicatorCount?: number;
	sx?: SxProps<Theme>;
	viewportSx?: SxProps<Theme>;
	swiperSx?: SxProps<Theme>;
	slideSx?: SxProps<Theme>;
	renderHeader?: (controls: SwiperCarouselControls) => React.ReactNode;
	renderFooter?: (controls: SwiperCarouselControls) => React.ReactNode;
};

const fullBleedSx = {
	width: '100%',
	maxWidth: '100%'
} satisfies SxProps<Theme>;

const SwiperCarousel = ({
	slides,
	spaceBetween = 4,
	indicatorCount = 0,
	sx,
	viewportSx,
	swiperSx,
	slideSx,
	renderHeader,
	renderFooter
}: SwiperCarouselProps) => {
	const theme = useTheme();
	const [swiper, setSwiper] = React.useState<SwiperInstance | null>(null);

	const [controls, setControls] = React.useState<SwiperCarouselControls>({
		canGoPrev: false,
		canGoNext: false,
		activeIndicator: 0,
		goPrev: () => undefined,
		goNext: () => undefined
	});

	const slideGap = React.useMemo(() => theme.spacing(spaceBetween), [spaceBetween, theme]);

	const updateControls = React.useCallback(
		(instance: SwiperInstance | null) => {
			if (!instance) return;

			const progress = Number.isFinite(instance.progress) ? instance.progress : 0;
			const activeIndicator =
				indicatorCount > 0 ? Math.min(indicatorCount - 1, Math.floor(progress * indicatorCount)) : 0;

			setControls((prev) => ({
				...prev,
				canGoPrev: !instance.isBeginning,
				canGoNext: !instance.isEnd,
				activeIndicator
			}));
		},
		[indicatorCount]
	);

	// In `cssMode` with `slidesPerView="auto"`, Swiper's slide-index API
	// can desync from actual scrollLeft. We scroll the wrapperEl (the native
	// scroll container in cssMode) directly by one viewport width and let
	// CSS scroll-snap re-align to the nearest slide.
	const goPrev = React.useCallback(() => {
		const el = swiper?.wrapperEl;
		if (!el) return;
		el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
	}, [swiper]);

	const goNext = React.useCallback(() => {
		const el = swiper?.wrapperEl;
		if (!el) return;
		el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
	}, [swiper]);

	React.useEffect(() => {
		setControls((prev) => ({
			...prev,
			goPrev,
			goNext
		}));
	}, [goNext, goPrev]);

	React.useEffect(() => {
		if (!swiper) return;
		swiper.update();
		updateControls(swiper);
	}, [swiper, updateControls]);

	return (
		<Box sx={[{ width: '100%' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
			{renderHeader?.(controls)}

			<Box
				sx={[
					fullBleedSx,
					{ overflow: 'visible' },
					...(Array.isArray(viewportSx) ? viewportSx : viewportSx ? [viewportSx] : [])
				]}
			>
				<Box
					sx={[
						(t) => ({
							containerType: 'inline-size',
							'& .swiper': {
								width: '100%',
								overflowX: 'auto',
								overflowY: 'hidden',
								WebkitOverflowScrolling: 'touch',
								scrollBehavior: 'smooth',
								scrollbarWidth: 'none',
								paddingTop: t.spacing(1),
								paddingBottom: t.spacing(2),
								scrollPaddingLeft: {
									xs: `calc(max(0px, (100cqi - ${t.layout.contentMaxWidthPx}px) / 2) + ${t.spacing(t.layout.pagePx.xs)})`,
									md: `calc(max(0px, (100cqi - ${t.layout.contentMaxWidthPx}px) / 2) + ${t.spacing(t.layout.pagePx.md)})`,
									lg: `calc(max(0px, (100cqi - ${t.layout.contentMaxWidthPx}px) / 2) + ${t.spacing(t.layout.pagePx.lg)})`
								},
								scrollPaddingRight: {
									xs: t.spacing(t.layout.pagePx.xs),
									md: t.spacing(4)
								}
							},
							'& .swiper::-webkit-scrollbar': {
								display: 'none'
							},
							'& .swiper-wrapper': {
								alignItems: 'stretch',
								columnGap: slideGap,
								overscrollBehaviorX: 'contain'
							},
							'& .swiper-slide': {
								width: 'auto',
								height: 'auto',
								flexShrink: 0,
								scrollSnapAlign: { xs: 'center', md: 'start' }
							}
						}),
						...(Array.isArray(swiperSx) ? swiperSx : swiperSx ? [swiperSx] : [])
					]}
				>
					<Swiper
						modules={[A11y]}
						cssMode
						slidesPerView="auto"
						slidesPerGroup={1}
						spaceBetween={0}
						allowTouchMove={slides.length > 1}
						watchOverflow
						observer
						observeParents
						a11y={{
							enabled: true,
							containerRoleDescriptionMessage: 'Karussell',
							itemRoleDescriptionMessage: 'Folie',
							slideLabelMessage: '{{index}} von {{slidesLength}}',
							// In cssMode the scroll container is wrapperEl;
							// the default handler resets el.scrollLeft = 0 on focus.
							scrollOnFocus: false
						}}
						onSwiper={(instance) => {
							setSwiper(instance);
							updateControls(instance);
						}}
						onProgress={(instance) => updateControls(instance)}
						onSlideChange={(instance) => updateControls(instance)}
						onResize={(instance) => updateControls(instance)}
					>
						{slides.map((slide, index) => (
							<SwiperSlide key={slide.key}>
								<Box
									sx={[
										{
											height: '100%',
											marginLeft:
												index === 0
													? {
															xs: `calc(max(0px, (100cqi - ${theme.layout.contentMaxWidthPx}px) / 2) + ${theme.spacing(theme.layout.pagePx.xs)})`,
															md: `calc(max(0px, (100cqi - ${theme.layout.contentMaxWidthPx}px) / 2) + ${theme.spacing(theme.layout.pagePx.md)})`,
															lg: `calc(max(0px, (100cqi - ${theme.layout.contentMaxWidthPx}px) / 2) + ${theme.spacing(theme.layout.pagePx.lg)})`
														}
													: undefined,
											marginRight: index === slides.length - 1 ? theme.spacing(4) : undefined
										},
										...(Array.isArray(slideSx) ? slideSx : slideSx ? [slideSx] : []),
										...(Array.isArray(slide.sx) ? slide.sx : slide.sx ? [slide.sx] : [])
									]}
								>
									{slide.content}
								</Box>
							</SwiperSlide>
						))}
					</Swiper>
				</Box>
			</Box>

			{renderFooter?.(controls)}
		</Box>
	);
};

export default SwiperCarousel;
